import { env } from '@/env.js';
import { apiRequest } from '../../lib/api/host-pool.js';

export interface PublicSiteConfig {
  tenantId: string;
  agentId: string;
  sitename: string;
  logoUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  defaultLocale: string | null;
  /** ISO 3166-1 alpha-2 — fixed phone country for the register form (null = TH). */
  phoneCountry: string | null;
  /** What the site shows money in. */
  currency: string;
  /** What the brand's money is held in; absent from a snapshot cached before it existed. */
  ledgerCurrency?: string;
  captcha: { enabled: boolean; siteKey: string | null };
  chatwoot: { baseUrl: string; websiteToken: string } | null;
  support: { lineId?: string } | null;
  /** The storefront's primary domain, where shared links point; absent from older snapshots. */
  primaryDomain?: string | null;
}

/** Hostname, lowercased, port + trailing dot stripped (matches backend canon). */
export function siteConfigHost(): string {
  return window.location.hostname.toLowerCase().replace(/\.$/, '');
}

/**
 * Fetch the white-label config for this origin. Public endpoint, no JWT.
 * Resolves by Host header (server reads it); query carries `?host=` so cold
 * edge + dev work. When VITE_TENANT_ID is set we resolve by tenantId instead.
 */
export async function fetchSiteConfig(): Promise<PublicSiteConfig> {
  const query = env.VITE_TENANT_ID
    ? `?tenantId=${encodeURIComponent(env.VITE_TENANT_ID)}`
    : `?host=${encodeURIComponent(siteConfigHost())}`;
  const headers: Record<string, string> = {};
  if (env.VITE_TENANT_ID) headers['x-tenant-id'] = env.VITE_TENANT_ID;
  const res = await apiRequest(`/site-config${query}`, { headers });
  if (!res.ok) {
    throw new Error(`site-config ${res.status}`);
  }
  return (await res.json()) as PublicSiteConfig;
}
